<template>
  <div class="material-score-input">
    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="danger" label="Cargando..."></b-spinner>
      <p class="mt-2 text-muted">Cargando preguntas...</p>
    </div>

    <div v-else-if="questions.length > 0">
      <p class="msi-help">
        Ingresa el resultado del estudiante en cada pregunta. Las preguntas con
        rúbrica se evalúan seleccionando el nivel logrado por criterio.
      </p>

      <div class="msi-questions">
        <div
          v-for="(q, idx) in questions"
          :key="q.question_id"
          ref="rows"
          class="msi-row"
          :class="{ 'msi-row--rubric': q.has_rubric }"
        >
          <div class="msi-statement">
            <span class="msi-index">{{ idx + 1 }}.</span>
            <span v-html="q.statement || 'Pregunta sin enunciado'"></span>
          </div>

          <!-- Rubric: pick a level per criterion via buttons -->
          <div v-if="q.has_rubric" class="msi-rubric">
            <div class="msi-rubric-total">
              Puntaje: <strong>{{ rubricScore(q) }}</strong> / {{ q.max_score }}
            </div>
            <div
              v-for="crit in q.criteria"
              :key="crit.criteria_id"
              class="msi-criterion"
            >
              <label class="msi-criterion-name">{{ crit.name }}</label>
              <div class="msi-level-buttons">
                <button
                  v-for="lvl in crit.levels"
                  :key="lvl.criteria_level_id"
                  type="button"
                  class="msi-level-btn"
                  :class="{
                    'msi-level-btn--active':
                      rubricSelections[q.question_id][crit.criteria_id] ===
                      lvl.criteria_level_id,
                  }"
                  :disabled="generating"
                  @click="
                    toggleLevel(
                      $event,
                      q.question_id,
                      crit.criteria_id,
                      lvl.criteria_level_id,
                      idx
                    )
                  "
                >
                  <span class="msi-level-label">{{ lvl.label }}</span>
                  <span class="msi-level-score">{{ lvl.score }} pts</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Non-rubric: pick the score with buttons (0..max_score). Clicking
               one sets the score and auto-advances to the next question. -->
          <div v-else class="msi-score-btns">
            <button
              v-for="n in scoreOptions(q)"
              :key="n"
              type="button"
              class="msi-score-btn"
              :class="{ 'msi-score-btn--active': scores[q.question_id] === n }"
              :disabled="generating"
              @click="setScore(q, n, idx)"
            >
              {{ n }}
            </button>
            <span class="msi-max">/ {{ q.max_score }}</span>
          </div>
        </div>
      </div>

      <!-- <div class="msi-observations">
        <label class="msi-label">Observaciones (opcional)</label>
        <textarea
          v-model="observations"
          class="msi-textarea"
          rows="3"
          placeholder="Comentarios para el informe..."
          :disabled="generating"
        ></textarea>
      </div> -->

      <div ref="footer" class="msi-footer">
        <button
          v-if="!done"
          class="msi-generate-btn"
          :disabled="generating"
          @click="generate"
        >
          <b-spinner v-if="generating" small></b-spinner>
          <span>{{ generating ? "Generando informe..." : "Generar informe" }}</span>
        </button>
        <button v-else class="msi-finish-btn" @click="finish">
          {{ isHost ? "Revisar Otro Estudiante" : "Finalizar" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import assistedMaterialService from "@/services/assistedMaterialService";
import customMaterialService from "@/services/customMaterialService";

export default {
  name: "MaterialScoreInput",
  props: {
    context: {
      type: Object,
      required: true,
    },
    canWork: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      loading: true,
      generating: false,
      done: false,
      questions: [],
      scores: {},
      rubricSelections: {},
      observations: "",
    };
  },
  computed: {
    token() {
      return this.$store.state.login.token;
    },
    conversation() {
      return this.$store.state.chatbot.conversation || {};
    },
    materialId() {
      // Both hosts publish the selected material to the chatbot store
      // (MaterialApplicationStep.syncCorrectionStore / ApplyMaterialModal).
      const sel = this.conversation.existing_materials_list;
      return sel ? sel.selectedMaterialId : null;
    },
    gradeName() {
      const g = this.conversation.grade;
      return g && g.answer ? g.answer.original_name : "";
    },
    // Redpie host: a student is chosen via context (chatbot has none).
    isHost() {
      return !!(this.context && this.context.studentGradeId);
    },
  },
  mounted() {
    // Guests can't fetch the scoring sheet (auth required); the vitrina shows a
    // login prompt instead. Skip the fetch so it doesn't 401.
    if (!this.canWork && !this.token) {
      this.loading = false;
      return;
    }
    this.fetchSheet();
  },
  watch: {
    // Load the questions once the user logs in while the demo is still mounted.
    token(newToken) {
      if (newToken && !this.questions.length) this.fetchSheet();
    },
  },
  methods: {
    async fetchSheet() {
      this.loading = true;
      try {
        if (!this.materialId) {
          this.questions = [];
          return;
        }
        const data = await assistedMaterialService.getMaterialScoringSheet(
          this.materialId
        );
        this.questions = data.questions || [];
        this.questions.forEach((q) => {
          if (q.has_rubric) {
            const perCriterion = {};
            (q.criteria || []).forEach((c) => {
              perCriterion[c.criteria_id] = null;
            });
            this.$set(this.rubricSelections, q.question_id, perCriterion);
          } else {
            this.$set(this.scores, q.question_id, 0);
          }
        });
      } catch (error) {
        console.error("Error fetching scoring sheet:", error);
        this.$toasted.error("No se pudieron cargar las preguntas.");
        this.questions = [];
      } finally {
        this.loading = false;
      }
    },
    // Clear the entered scores for the next student WITHOUT refetching the
    // sheet (keeps the same material loaded — no reload/flash).
    reset() {
      this.done = false;
      this.generating = false;
      this.scores = {};
      this.rubricSelections = {};
      this.questions.forEach((q) => {
        if (q.has_rubric) {
          const perCriterion = {};
          (q.criteria || []).forEach((c) => {
            perCriterion[c.criteria_id] = null;
          });
          this.$set(this.rubricSelections, q.question_id, perCriterion);
        } else {
          this.$set(this.scores, q.question_id, 0);
        }
      });
    },
    // Integer score options 0..max_score for a non-rubric question.
    scoreOptions(q) {
      const max = Math.floor(Number(q.max_score) || 0);
      const opts = [];
      for (let i = 0; i <= max; i += 1) opts.push(i);
      return opts;
    },
    // Set a non-rubric score via the buttons, then auto-advance to the next
    // question so the user can keep clicking down the list.
    setScore(q, n, idx) {
      this.$set(this.scores, q.question_id, n);
      this.$nextTick(() => this.scrollToNext(idx));
    },
    scrollToNext(idx) {
      const rows = this.$refs.rows || [];
      const next = rows[idx + 1];
      if (next && next.scrollIntoView) {
        next.scrollIntoView({ behavior: "smooth", block: "center" });
      } else if (this.$refs.footer && this.$refs.footer.scrollIntoView) {
        this.$refs.footer.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    },
    toggleLevel(event, questionId, criteriaId, levelId, rowIdx) {
      const selection = this.rubricSelections[questionId];
      if (!selection) return;
      // Single level per criterion; clicking the active one clears it.
      const next = selection[criteriaId] === levelId ? null : levelId;
      this.$set(selection, criteriaId, next);
      if (next == null) return; // clearing: don't advance
      // Autoscroll to the next criterion in this question, else to the next
      // question row (which may be another rubric or a simple score input).
      const criterion = event.currentTarget.closest(".msi-criterion");
      this.$nextTick(() => {
        const sib = criterion && criterion.nextElementSibling;
        if (sib && sib.classList.contains("msi-criterion")) {
          sib.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
          this.scrollToNext(rowIdx);
        }
      });
    },
    rubricScore(q) {
      const selection = this.rubricSelections[q.question_id] || {};
      let total = 0;
      (q.criteria || []).forEach((crit) => {
        const selectedId = selection[crit.criteria_id];
        if (selectedId == null) return;
        const lvl = crit.levels.find(
          (l) => l.criteria_level_id === selectedId
        );
        if (lvl) total += Number(lvl.score) || 0;
      });
      return total;
    },
    // Same payload for every flow: rubric questions send the chosen level per
    // criterion (the backend sums them), never a bare total — the report needs
    // the levels to write each criterion's paragraph.
    buildScores() {
      return this.questions.map((q) => {
        if (q.has_rubric) {
          const selection = this.rubricSelections[q.question_id] || {};
          const criteriaLevels = Object.values(selection).filter(
            (v) => v !== null && v !== undefined
          );
          return {
            question_id: q.question_id,
            criteria_levels: criteriaLevels,
          };
        }
        const raw = Number(this.scores[q.question_id]) || 0;
        const bounded = Math.min(Math.max(raw, 0), q.max_score);
        return {
          question_id: q.question_id,
          score: bounded,
        };
      });
    },
    generate() {
      this.generating = true;
      // Host passed an existing MaterialLink (ApplyMaterialModal): save the
      // scores to it and generate the report directly.
      const materialLinkId = this.context && this.context.materialLinkId;
      if (materialLinkId) {
        this.generateForLink(materialLinkId);
        return;
      }
      // Host (redpie) with a chosen student: associate the report to that
      // student's StudentGradeDocumentType and store it in Drive instead of
      // downloading a studentless copy.
      const studentGradeId = this.context && this.context.studentGradeId;
      if (studentGradeId) {
        this.generateForStudent(studentGradeId);
        return;
      }
      const payload = {
        material: this.materialId,
        grade_name: this.gradeName,
        observations: this.observations,
        scores: this.buildScores(),
      };
      assistedMaterialService
        .generateMaterialReport(payload)
        .then((response) => {
          let filename = "Informe_Material.docx";
          const disposition = response.headers["content-disposition"];
          if (disposition && disposition.indexOf("attachment") !== -1) {
            const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
              disposition
            );
            if (matches != null && matches[1]) {
              filename = matches[1].replace(/['"]/g, "");
            }
          }
          const blob = new Blob([response.data], {
            type: response.headers["content-type"],
          });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
          this.done = true;
        })
        .catch((error) => {
          this.$toasted.error(
            "Error al generar el informe: " +
              (error.response ? error.response.status : error.message)
          );
        })
        .finally(() => {
          this.generating = false;
        });
    },
    // Resolve the target document type; warn if there is none, or ask before
    // replacing an existing document. Then build the MaterialLink (+ the
    // StudentGradeDocumentTypeMaterialResponse reference), persist the scores
    // and generate the report on Drive under that document type.
    generateForStudent(studentGradeId) {
      const year = this.context && this.context.year;
      const scores = this.buildScores();
      customMaterialService
        .resolveMaterialReportTarget({
          material_id: this.materialId,
          student_grade: studentGradeId,
          year,
        })
        .then((target) => {
          if (!target.ok) {
            this.$toasted.error(
              "No existe un tipo de documento para esta prueba. Contacta al administrador."
            );
            return null;
          }
          let replace = false;
          if (target.has_active_document) {
            if (
              !window.confirm(
                "Ya existe un informe para este estudiante en este documento. ¿Deseas reemplazarlo?"
              )
            ) {
              return null;
            }
            replace = true;
          }
          return customMaterialService
            .getOrCreateMaterialLink({
              material_id: this.materialId,
              student_grade: studentGradeId,
              year,
              replace,
            })
            .then((link) =>
              customMaterialService
                .saveManualScores(link.id, scores)
                .then(() => customMaterialService.generateReport(link.id))
            )
            .then((data) => {
              this.$toasted.success(
                (data && data.message) ||
                  "Informe generado y asociado al estudiante."
              );
              this.done = true;
              // Async upload: register the sgdt we're awaiting; the host opens
              // the report when the `report_ready` socket arrives.
              if (data && data.sgdt_id) this.$emit("await-report", data.sgdt_id);
            });
        })
        .catch((error) => {
          this.$toasted.error(
            "No se pudo generar el informe: " +
              (error.response ? error.response.status : error.message)
          );
        })
        .finally(() => {
          this.generating = false;
        });
    },
    // ApplyMaterialModal flow: save scores to the existing link and generate
    // the report on it (the link + SGDT reference were built beforehand).
    generateForLink(materialLinkId) {
      customMaterialService
        .saveManualScores(materialLinkId, this.buildScores())
        .then(() => customMaterialService.generateReport(materialLinkId))
        .then((data) => {
          this.$toasted.success(
            (data && data.message) || "Informe generado exitosamente."
          );
          this.done = true;
          if (data && data.sgdt_id) this.$emit("await-report", data.sgdt_id);
        })
        .catch((error) => {
          this.$toasted.error(
            "No se pudo generar el informe: " +
              (error.response ? error.response.status : error.message)
          );
        })
        .finally(() => {
          this.generating = false;
        });
    },
    finish() {
      // Redpie: send the user back up to pick another student instead of the
      // chatbot's "report_done" end step.
      if (this.isHost) {
        this.$emit("review-another");
        return;
      }
      this.$emit("go-to-step", "report_done");
    },
  },
};
</script>

<style scoped>
.material-score-input {
  padding: 10px 0;
  width: 100%;
}
.msi-help {
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
}
.msi-questions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* Show many questions at once; only scroll past very long materials. */
  max-height: 90vh;
  overflow-y: auto;
  padding-right: 6px;
}
.msi-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
}
.msi-row--rubric {
  flex-direction: column;
  align-items: stretch;
}
.msi-statement {
  flex: 1;
  font-size: 14px;
  color: #1f2937;
}
.msi-index {
  font-weight: 700;
  margin-right: 6px;
}
.msi-score-btns {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}
.msi-score-btn {
  min-width: 38px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 6px 10px;
  font-weight: 700;
  color: #1f2937;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s, color 0.12s;
}
.msi-score-btn:hover:not(:disabled) {
  border-color: #fca5a5;
}
.msi-score-btn--active {
  border-color: #ef4444;
  background: #ef4444;
  color: #fff;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.35);
}
.msi-score-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.msi-max {
  color: #6b7280;
  font-weight: 600;
}
.msi-rubric {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.msi-criterion {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.msi-criterion-name {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  margin: 0;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.msi-rubric-total {
  align-self: flex-end;
  font-size: 13px;
  color: #374151;
}
.msi-rubric-total strong {
  color: #ef4444;
}
.msi-level-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.msi-level-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 6px 10px;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s;
}
.msi-level-btn:hover:not(:disabled) {
  border-color: #fca5a5;
}
.msi-level-btn--active {
  border-color: #ef4444;
  background: #ef4444;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.35);
}
.msi-level-btn--active .msi-level-label,
.msi-level-btn--active .msi-level-score {
  color: #fff;
}
.msi-level-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.msi-level-label {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
}
.msi-level-score {
  font-size: 12px;
  color: #6b7280;
}
.msi-observations {
  margin-top: 14px;
}
.msi-label {
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}
.msi-textarea {
  width: 100%;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px;
  resize: vertical;
}
.msi-textarea:focus {
  outline: none;
  border-color: #ef4444;
}
.msi-footer {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
.msi-generate-btn,
.msi-finish-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 10px;
  padding: 10px 20px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  background: #ef4444;
}
.msi-finish-btn {
  background: #10b981;
}
.msi-generate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
